import {checkAuthorizations as legacyCheckAuthorizations} from './checkAuthorizations';
import {DesignerStores} from '@packages/systems/designer/flux';
import {
  AuthorizationAction,
  AuthorizationData,
  AuthorizationResourceType,
  isOsoResourceType,
} from '@packages/domains/identity-and-access/role-based-access/data-types';

export type AuthorizationTests = Record<string, boolean>;

const supportedResources = [
  'SiteCollection',
  'SiteLocale',
  'SitePage',
  'SiteBranch',
  'SitePageSettings',
] as const;

type SupportedAuthorizationResource = (typeof supportedResources)[number];
type ResourceContext = Partial<Record<SupportedAuthorizationResource, string>>;

export type AuthorizationOptions = Partial<
  Record<SupportedAuthorizationResource, {currentResourceId?: string}>
>;
export type LegacyAuthorizationOptions = {
  currentCollectionId?: string;
};

export type AuthorizationStateSlice = Pick<
  DesignerStores,
  'AuthorizationsStore' | 'CurrentCollectionStore' | 'SiteDataStore'
>;

/**
 * Parses an authorization string like "SiteCollection/read" into resource type and action
 */
export function parseAuthorizationString(authorizationString: string): {
  resourceType: AuthorizationResourceType;
  action: AuthorizationAction;
} | null {
  const parts = authorizationString.split('/');
  if (parts.length === 2 && parts[0] && parts[1]) {
    if (!isOsoResourceType(parts[0])) {
      return null;
    }
    return {
      resourceType: parts[0] as AuthorizationResourceType,
      action: parts[1] as AuthorizationAction,
    };
  }
  return null;
}

/**
 * Checks if user has access to a specific resource/action combination
 */
export function hasResourceAccess(
  userAuthData: Record<string, AuthorizationData>,
  resourceType: AuthorizationResourceType,
  action: AuthorizationAction,
  resourceId: string
): boolean {
  const resourceData = userAuthData[resourceType];
  if (!resourceData) return true;

  // Check specific resource permissions first
  if (resourceId) {
    const specificData = resourceData[resourceId];
    if (specificData?.authorizedActions) {
      return specificData.authorizedActions.includes(action);
    }
  }

  // Fall back to default permissions
  const defaultData = resourceData.DEFAULT_ACTIONS;
  if (defaultData?.authorizedActions) {
    return defaultData.authorizedActions.includes(action);
  }

  // If no permissions data found, permissive by default
  return true;
}

/**
 * Evaluates authorization tests against new system data
 */
export function evaluateUserAuthorizations(
  authorizationTests: AuthorizationTests,
  userAuthData: Record<string, AuthorizationData>,
  resourceContext: ResourceContext
): boolean {
  for (const [authString, expectedAccess] of Object.entries(
    authorizationTests
  )) {
    const parsed = parseAuthorizationString(authString);
    if (!parsed) continue;

    if (!isSupportedResource(parsed.resourceType)) continue;

    const resourceId = resourceContext[parsed.resourceType];
    if (!resourceId) continue;

    const actualAccess = hasResourceAccess(
      userAuthData,
      parsed.resourceType,
      parsed.action,
      resourceId
    );

    if (actualAccess !== expectedAccess) return false;
  }

  return true;
}

type CheckSiteAuthorizationsOptions = {
  legacyAuthorizationOptions: LegacyAuthorizationOptions;
  authorizationOptions: AuthorizationOptions;
};

const SUPPORTED_RESOURCES: readonly SupportedAuthorizationResource[] =
  supportedResources;

const isSupportedResource = (
  resourceType: AuthorizationResourceType
): resourceType is SupportedAuthorizationResource =>
  (SUPPORTED_RESOURCES as readonly AuthorizationResourceType[]).includes(
    resourceType
  );

const buildResourceContext = (options: AuthorizationOptions = {}) =>
  SUPPORTED_RESOURCES.reduce<ResourceContext>((accumulator, resourceType) => {
    const resourceId = options[resourceType]?.currentResourceId;
    if (resourceId) {
      accumulator[resourceType] = resourceId;
    }
    return accumulator;
  }, {});

/**
 * Two-level memoization cache for authorization checks.
 *
 * This avoids expensive JSON.stringify on large userAuthData objects (which can
 * contain 600+ page entries). Instead, we use object identity via WeakMap.
 *
 * Level 1: WeakMap keyed by userAuthData object reference
 *   - Auto-invalidates when store updates create new object references
 * Level 2: Map keyed by tests + resource context string
 *   - Enables O(1) lookups for repeated checks with same context
 *   - Effectively bounded by (pages × auth types) since store updates
 *     create new userAuthData references, triggering full cleanup via WeakMap
 */
const resultCache = new WeakMap<
  Record<string, AuthorizationData>,
  Map<string, boolean>
>();
const testsKeyCache = new WeakMap<AuthorizationTests, string>();

function buildTestsKey(tests: AuthorizationTests): string {
  let key = testsKeyCache.get(tests);
  if (key === undefined) {
    key = Object.entries(tests)
      .sort(([keyA], [keyB]) => keyA.localeCompare(keyB))
      .map(([k, v]) => `${k}:${v}`)
      .join(',');
    testsKeyCache.set(tests, key);
  }
  return key;
}

/**
 * Builds a cache key from resource context.
 * Comma separator is safe since resource IDs are MongoDB ObjectIds (24 hex chars).
 */
function buildContextKey(ctx: ResourceContext): string {
  return SUPPORTED_RESOURCES.map((r) => ctx[r] ?? '').join(',');
}

function getCachedResult(
  tests: AuthorizationTests,
  userAuthData: Record<string, AuthorizationData>,
  context: ResourceContext
): boolean {
  let cache = resultCache.get(userAuthData);
  if (!cache) {
    cache = new Map();
    resultCache.set(userAuthData, cache);
  }

  const key = `${buildTestsKey(tests)}|${buildContextKey(context)}`;
  let result = cache.get(key);

  if (result === undefined) {
    result = evaluateUserAuthorizations(tests, userAuthData, context);
    cache.set(key, result);
  }

  return result;
}

/**
 * Main authorizations validator for Designer Abilities
 */
export const checkSiteAuthorizations =
  (authorizationTests: AuthorizationTests) =>
  (
    state: AuthorizationStateSlice,
    {
      legacyAuthorizationOptions,
      authorizationOptions,
    }: CheckSiteAuthorizationsOptions
  ): boolean => {
    const {
      AuthorizationsStore,
      SiteDataStore: {userId},
    } = state;

    const hasNewFormat = Object.keys(authorizationTests).some((key) =>
      key.includes('/')
    );

    if (hasNewFormat) {
      const siteAuthDataLoaded = Boolean(
        AuthorizationsStore.siteAuthorizations?.hasLoaded
      );
      // permissive by default when data isn't loaded
      if (!siteAuthDataLoaded) return true;

      const userAuthorizationsData =
        AuthorizationsStore.siteAuthorizations?.data?.[userId];

      if (!userAuthorizationsData) {
        return true;
      }

      return getCachedResult(
        authorizationTests,
        userAuthorizationsData,
        buildResourceContext(authorizationOptions)
      );
    }

    // fallback to legacy format
    const legacyUserAuthorizationsData =
      AuthorizationsStore.authorizations?.[userId];
    return legacyUserAuthorizationsData
      ? legacyCheckAuthorizations(authorizationTests)(
          state,
          legacyAuthorizationOptions || {}
        )
      : true;
  };
